import { computeGeometricAdjacency } from '../adjacency.js';
import { boundaryLoopOfFaceSet } from '../boundary.js';
import { parseTopology } from '../core.js';
import { edgeId, faceNormal, propagateExplicitEdgeIds } from '../edges.js';
import { assertSafeIdCapacity } from '../ids.js';
import { discoverLoopCutTraversal } from '../loop-discovery.js';
import { discoverLogicalQuadCells } from '../quads.js';
import { assertRenderableTriangle } from '../validation.js';
import { cross3, dot3, sub3 } from '../vector-math.js';

const WINDING_EPSILON = 1e-9;

function compareIds(a, b) {
  return a < b ? -1 : a > b ? 1 : 0;
}

function rotate(values, index) {
  return [...values.slice(index), ...values.slice(0, index)];
}

function rawEdgeInfoById(topology) {
  const result = new Map();
  for (const face of [...topology.faces].sort((a, b) => compareIds(a.id, b.id))) {
    for (let index = 0; index < face.vertexIds.length; index++) {
      const a = face.vertexIds[index];
      const b = face.vertexIds[(index + 1) % face.vertexIds.length];
      const rawEdgeId = edgeId(a, b);
      const existing = result.get(rawEdgeId);
      if (existing) existing.faceIds.add(face.id);
      else result.set(rawEdgeId, { a, b, faceIds: new Set([face.id]) });
    }
  }
  return result;
}

function rawBoundaryEdgeIdForGeometricEdge(cell, geometricEdgeId, geometricEdgeIdByRawEdgeId) {
  const matches = cell.boundaryTopologyEdgeIds.filter((rawEdgeId) => (
    geometricEdgeIdByRawEdgeId.get(rawEdgeId) === geometricEdgeId
  ));
  if (matches.length !== 1) {
    throw new Error(
      `loopCut logical cell '${cell.id}' has ${matches.length} raw boundary edges for geometric edge '${geometricEdgeId}'`,
    );
  }
  return matches[0];
}

function woundLoopForCell(cell, faceById, entryRawEdgeId, exitRawEdgeId) {
  const faces = cell.faceIds.map((faceId) => {
    const face = faceById.get(faceId);
    if (!face) throw new Error(`loopCut logical cell '${cell.id}' references unknown face '${faceId}'`);
    return face;
  });
  const loop = boundaryLoopOfFaceSet(faces, 'loopCut');
  if (loop.length !== 4 || new Set(loop).size !== 4) {
    throw new Error(`loopCut logical cell '${cell.id}' does not have a simple four-corner boundary`);
  }
  const entryIndex = loop.findIndex((vertexId, index) => (
    edgeId(vertexId, loop[(index + 1) % loop.length]) === entryRawEdgeId
  ));
  if (entryIndex === -1) {
    throw new Error(`loopCut logical cell '${cell.id}' cannot orient entry raw edge '${entryRawEdgeId}'`);
  }
  const oriented = rotate(loop, entryIndex);
  if (edgeId(oriented[2], oriented[3]) !== exitRawEdgeId) {
    throw new Error(`loopCut logical cell '${cell.id}' exit raw edge '${exitRawEdgeId}' is not opposite its entry`);
  }
  return oriented;
}

function assertChildWinding(commandName, childVertexIds, positionById, expectedNormal, label) {
  const positions = childVertexIds.map((vertexId) => positionById.get(vertexId));
  if (positions.some((position) => position === undefined)) {
    throw new Error(`${commandName}: ${label} references an unknown vertex`);
  }
  assertRenderableTriangle(commandName, label, positions);
  const [a, b, c] = positions;
  const normal = cross3(sub3(b, a), sub3(c, a));
  const length = Math.hypot(...normal);
  if (!(length > 0) || !Number.isFinite(length) || dot3(normal.map((value) => value / length), expectedNormal) < 1 - WINDING_EPSILON) {
    throw new Error(`${commandName}: ${label} would reverse its source winding`);
  }
}

function computedResultSummary(computed) {
  return {
    status: computed.traversal.status,
    startEdgeId: computed.traversal.startEdgeId,
    cellIds: computed.traversal.cellIds,
    crossedEdgeIds: computed.traversal.crossedEdgeIds,
    newVertexIds: computed.newVertices.map((vertex) => vertex.id),
    newFaceIds: computed.newFaces.map((face) => face.id),
    removedFaceIds: computed.removedFaceIds,
    cutTopologyEdgeIds: computed.cutTopologyEdgeIds,
  };
}

/**
 * Compute a fixed midline Loop Cut without mutating `topology`. The caller
 * may reuse the returned plan with `commitLoopCut`; `loopCut` below is the
 * ordinary all-or-nothing convenience entry point.
 */
export function computeLoopCut(topology, startEdgeId) {
  const commandName = 'loopCut';
  const adjacency = computeGeometricAdjacency(topology);
  const logicalCells = discoverLogicalQuadCells(topology, adjacency);
  const traversal = discoverLoopCutTraversal(topology, startEdgeId, { adjacency, logicalCells });
  const cellById = new Map(logicalCells.cells.map((cell) => [cell.id, cell]));
  const faceById = new Map(topology.faces.map((face) => [face.id, face]));
  const positionById = new Map(topology.vertices.map((vertex) => [vertex.id, vertex.position]));
  const geometricEdgeById = new Map(adjacency.edges.map((edge) => [edge.id, edge]));
  const geometricEdgeIdByRawEdgeId = new Map();
  for (const edge of adjacency.edges) {
    for (const rawEdgeId of edge.topologyEdgeIds) {
      const previous = geometricEdgeIdByRawEdgeId.get(rawEdgeId);
      if (previous && previous !== edge.id) {
        throw new Error(`${commandName}: raw edge '${rawEdgeId}' maps to multiple geometric edges`);
      }
      geometricEdgeIdByRawEdgeId.set(rawEdgeId, edge.id);
    }
  }

  const rawInfoById = rawEdgeInfoById(topology);
  const sourceFaceIdSet = new Set();
  for (const cellId of traversal.cellIds) {
    const cell = cellById.get(cellId);
    if (!cell) throw new Error(`${commandName}: traversal references unknown logical cell '${cellId}'`);
    for (const faceId of cell.faceIds) {
      if (sourceFaceIdSet.has(faceId)) throw new Error(`${commandName}: traversal reuses source face '${faceId}'`);
      sourceFaceIdSet.add(faceId);
    }
  }
  const midpointSpecs = [];
  const midpointSpecByRawEdgeId = new Map();
  for (const geometricEdgeId of traversal.crossedEdgeIds) {
    const geometricEdge = geometricEdgeById.get(geometricEdgeId);
    if (!geometricEdge || !Array.isArray(geometricEdge.topologyEdgeIds) || geometricEdge.topologyEdgeIds.length === 0) {
      throw new Error(`${commandName}: traversal edge '${geometricEdgeId}' is missing raw topology edge ids`);
    }
    for (const rawEdgeId of [...geometricEdge.topologyEdgeIds].sort(compareIds)) {
      if (midpointSpecByRawEdgeId.has(rawEdgeId)) {
        throw new Error(`${commandName}: traversal reuses raw edge '${rawEdgeId}'`);
      }
      const rawInfo = rawInfoById.get(rawEdgeId);
      if (!rawInfo) throw new Error(`${commandName}: traversal references missing raw edge '${rawEdgeId}'`);
      for (const incidentFaceId of rawInfo.faceIds) {
        if (!sourceFaceIdSet.has(incidentFaceId)) {
          throw new Error(
            `${commandName}: cannot split raw edge '${rawEdgeId}' without conforming untouched face '${incidentFaceId}'`,
          );
        }
      }
      const spec = { rawEdgeId, ...rawInfo };
      midpointSpecs.push(spec);
      midpointSpecByRawEdgeId.set(rawEdgeId, spec);
    }
  }

  const faceMintCount = traversal.cellIds.length * 4;
  assertSafeIdCapacity(topology.nextVertexId, midpointSpecs.length, topology.nextFaceId, faceMintCount, commandName);

  let nextVertexId = topology.nextVertexId;
  const newVertices = midpointSpecs.map((spec) => {
    const a = positionById.get(spec.a);
    const b = positionById.get(spec.b);
    if (!a || !b) throw new Error(`${commandName}: raw edge '${spec.rawEdgeId}' references an unknown vertex`);
    const vertex = {
      id: `v${nextVertexId++}`,
      position: [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2, (a[2] + b[2]) / 2],
    };
    positionById.set(vertex.id, vertex.position);
    spec.midpointId = vertex.id;
    return vertex;
  });

  const explicitReplacements = new Map();
  for (const spec of midpointSpecs) {
    explicitReplacements.set(spec.rawEdgeId, [edgeId(spec.a, spec.midpointId), edgeId(spec.midpointId, spec.b)]);
  }
  const propagatedExplicitEdgeIds = propagateExplicitEdgeIds(topology.explicitEdgeIds, explicitReplacements);

  let nextFaceId = topology.nextFaceId;
  const newFaces = [];
  const removedFaceIdSet = new Set();
  const cutTopologyEdgeIds = [];
  for (let index = 0; index < traversal.cellIds.length; index++) {
    const cellId = traversal.cellIds[index];
    const cell = cellById.get(cellId);
    if (!cell) throw new Error(`${commandName}: traversal references unknown logical cell '${cellId}'`);
    const entryGeometricEdgeId = traversal.crossedEdgeIds[index];
    const exitGeometricEdgeId = traversal.crossedEdgeIds[(index + 1) % traversal.crossedEdgeIds.length];
    const entryRawEdgeId = rawBoundaryEdgeIdForGeometricEdge(cell, entryGeometricEdgeId, geometricEdgeIdByRawEdgeId);
    const exitRawEdgeId = rawBoundaryEdgeIdForGeometricEdge(cell, exitGeometricEdgeId, geometricEdgeIdByRawEdgeId);
    const entryMidpointId = midpointSpecByRawEdgeId.get(entryRawEdgeId)?.midpointId;
    const exitMidpointId = midpointSpecByRawEdgeId.get(exitRawEdgeId)?.midpointId;
    if (!entryMidpointId || !exitMidpointId) {
      throw new Error(`${commandName}: logical cell '${cell.id}' is missing a planned boundary midpoint`);
    }

    const [a, b, c, d] = woundLoopForCell(cell, faceById, entryRawEdgeId, exitRawEdgeId);
    const sourceNormal = faceNormal(topology, cell.faceIds[0]);
    for (const faceId of cell.faceIds.slice(1)) {
      if (dot3(faceNormal(topology, faceId), sourceNormal) < 1 - WINDING_EPSILON) {
        throw new Error(`${commandName}: logical cell '${cell.id}' source faces do not share one winding`);
      }
    }
    const childVertexIds = [
      [a, entryMidpointId, exitMidpointId],
      [a, exitMidpointId, d],
      [entryMidpointId, b, c],
      [entryMidpointId, c, exitMidpointId],
    ];
    for (const [childIndex, vertexIds] of childVertexIds.entries()) {
      assertChildWinding(commandName, vertexIds, positionById, sourceNormal, `cell '${cell.id}' child ${childIndex + 1}`);
      newFaces.push({ id: `f${nextFaceId++}`, vertexIds });
    }
    for (const faceId of cell.faceIds) {
      if (removedFaceIdSet.has(faceId)) throw new Error(`${commandName}: traversal reuses source face '${faceId}'`);
      removedFaceIdSet.add(faceId);
    }
    cutTopologyEdgeIds.push(edgeId(entryMidpointId, exitMidpointId));
  }

  const explicitEdgeIds = [...new Set([...(propagatedExplicitEdgeIds ?? []), ...cutTopologyEdgeIds])].sort(compareIds);
  const vertices = [...topology.vertices, ...newVertices]
    .map((vertex) => ({ id: vertex.id, position: [...vertex.position] }))
    .sort((a, b) => compareIds(a.id, b.id));
  const faces = [
    ...topology.faces.filter((face) => !removedFaceIdSet.has(face.id)),
    ...newFaces,
  ]
    .map((face) => ({ id: face.id, vertexIds: [...face.vertexIds] }))
    .sort((a, b) => compareIds(a.id, b.id));
  const validated = parseTopology({ nextVertexId, nextFaceId, vertices, faces, explicitEdgeIds });

  return {
    traversal,
    removedFaceIds: [...removedFaceIdSet].sort(compareIds),
    newVertices,
    newFaces,
    cutTopologyEdgeIds: [...new Set(cutTopologyEdgeIds)].sort(compareIds),
    vertices: validated.vertices,
    faces: validated.faces,
    explicitEdgeIds: validated.explicitEdgeIds,
    nextVertexId: validated.nextVertexId,
    nextFaceId: validated.nextFaceId,
  };
}

/** Apply a fully validated `computeLoopCut` plan at one mutation point. */
export function commitLoopCut(topology, computed) {
  topology.vertices = computed.vertices;
  topology.faces = computed.faces;
  topology.explicitEdgeIds = computed.explicitEdgeIds;
  topology.nextVertexId = computed.nextVertexId;
  topology.nextFaceId = computed.nextFaceId;
}

/** Compute and commit the fixed t=0.5 Loop Cut atomically. */
export function loopCut(topology, startEdgeId) {
  const computed = computeLoopCut(topology, startEdgeId);
  commitLoopCut(topology, computed);
  return computedResultSummary(computed);
}
